import {SettingOutlined, SafetyCertificateOutlined} from '@ant-design/icons';
import {
    Button, Checkbox, Popover, Spin, Table,
} from 'antd';
import Column, {ColumnProps} from 'antd/es/table/Column';
import cn from 'classnames';
import React, {
    useContext, FC, Dispatch, SetStateAction, Key,
} from 'react';

import {EReportConfigurationTabKey} from 'components/report-configuration/report-configuration.constants';
import {ReportConfigurationContext} from 'components/report-configuration/report-configuration.context';
import {IPaginationArgs} from 'shared/hooks/use-pagination';
import {useQueryParams} from 'shared/utils/query-params/use-query-params.hook';
import {useAppSelector} from 'store/config/hooks';
import {IReportDdReferenceRule} from 'store/slices/report-configuration-slice/dd-references-configuration';
import {ddReferenceRulesSelectors} from 'store/slices/report-configuration-slice/report-configuration-slice';

import {DDGroupingSettingsModalOpener} from '../../report-table-settings/modals/dd-grouping-settings-modal-opener';
import {DdReferencesEditModalOpener} from '../dd-references-edit-modal-opener';
import {ReportDdReferenceRulesContext} from '../report-dd-references-utils';
import {ExtraAttributesModalOpener} from './extra-attributes-modal-opener';

import './dd-references-table.less';

interface IDdReferenceTableColumn<T=string> {
    key: T;
    title: string; formatter?:
    ColumnProps<any>['render'];
    width?: string | number;
}
interface IDdReferencesTableProps {
    pagination: IPaginationArgs;
    selection: {
        selectedRowKeys: number[];
        setSelectedRowKeys: Dispatch<SetStateAction<number[]>>;
    };
}

export const DdReferencesTable: FC<IDdReferencesTableProps> = ({
    pagination,
    selection,
}: IDdReferencesTableProps) => {
    const {current, handleChange, pageSize} = pagination;
    const {selectedRowKeys, setSelectedRowKeys} = selection;

    const drilldownReferenceRules = useAppSelector(ddReferenceRulesSelectors.selectAll);
    const drilldownReferenceRulesTotalCount = useAppSelector(
        ddReferenceRulesSelectors.selectDdReferenceRulesTotalCount,
    );

    const {isLoading} = useContext(ReportDdReferenceRulesContext);
    const {setSelectedTabKey} = useContext(ReportConfigurationContext);

    const {updateQueryParams} = useQueryParams();

    const columns: IDdReferenceTableColumn<string | keyof IReportDdReferenceRule>[] = [{
        key: 'id',
        title: 'Правило связи (ID)',
        formatter: (value, record) => (
            <div>
                <DdReferencesEditModalOpener
                    defaultInitialValues={record}
                    component={{
                        text: value,
                        type: 'link',
                    }}
                    mode="edit"
                    extraPathsToPickData={['id']}
                />
            </div>
        ),
    },
    {
        key: 'Restrictions',
        title: '',
        formatter: (_, record) => (
            <Popover content="Настройка ограничений">
                <Button
                    className={cn('button-only-icon', 'button-column')}
                    type="default"
                    onClick={() => {
                        const {id} = record;
                        if (id) {
                            updateQueryParams([{name: 'drillDownId', value: id}], {action: 'replace'});
                        }
                        setSelectedTabKey?.(EReportConfigurationTabKey.REPORT_DD_RESTRICTIONS);
                    }}
                >
                    <SafetyCertificateOutlined />
                </Button>
            </Popover>
        ),
    },
    {
        key: 'Rules',
        title: '',
        formatter: (_, record) => (
            <Popover content="Настройка правил фильтрации">
                <Button
                    className={cn('button-only-icon', 'button-column')}
                    type="default"
                    onClick={() => {
                        const {id} = record;
                        if (id) {
                            updateQueryParams([{name: 'drillDownId', value: id}], {action: 'replace'});
                        }
                        setSelectedTabKey?.(EReportConfigurationTabKey.REPORT_DD_FILTERS);
                    }}
                >
                    <SettingOutlined />
                </Button>
            </Popover>
        ),
    },
    {
        key: 'parentDocumentType',
        title: 'Исходный документ',
        formatter: value => value?.meaning ?? '-',
    },
    {
        key: 'childDocumentType',
        title: 'Конечный документ',
        formatter: value => value?.meaning ?? '-',
    },
    {
        key: 'context',
        title: 'Доп. атрибуты',
        formatter: (_, record: IReportDdReferenceRule) => <ExtraAttributesModalOpener referenceRuleRecord={record} />,
    },
    {
        key: 'enabled',
        title: 'Признак активности',
        formatter: value => (
            <Checkbox
                disabled
                checked={value}
            />
        ),
    },
    {
        key: 'id',
        title: 'Настройка итоговой строки',
        formatter: (v, r, index) => (
            <DDGroupingSettingsModalOpener
                drilldownReferenceRules={drilldownReferenceRules}
                index={index}
            />
        ),
    },
    ];

    return (
        <Spin spinning={isLoading}>
            <Table
                className={cn('dd-references-table')}
                rowKey="id"
                scroll={{x: 'max-content'}}
                rowSelection={{
                    selectedRowKeys,
                    onChange: (newSelectedRowKeys: Key[]) => {
                        setSelectedRowKeys(newSelectedRowKeys as number[]);
                    },
                }}
                pagination={{
                    position: ['bottomLeft'],
                    total: drilldownReferenceRulesTotalCount,
                    current,
                    pageSize,
                    onChange: handleChange,
                }}
                dataSource={drilldownReferenceRules}
            >
                {
                    columns.map(({
                        key, title, formatter, width,
                    }) => (
                        <Column
                            dataIndex={key}
                            key={key}
                            title={title}
                            render={formatter}
                            width={width}
                        />
                    ))
                }
            </Table>
        </Spin>

    );
};
